import React from 'react';
import PropTypes from 'prop-types';
import {computed, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as Sentry from '@sentry/react';

import {withI18n} from 'spa/Components/Context';
import { withRouter, routerShape } from 'react-router';
import isValidException from 'utils/isValidException';

import { IconSizes } from '@urbanmassage/component-library/lib/Theme/b2c-theme';
import StarRatingGroup, {
  RatingFontSizeOptions,
  RatingFontWeight,
} from '@urbanmassage/component-library/lib/StarRatingGroup/StarRatingGroup';
import Typography, {TypeVariants} from '@urbanmassage/component-library/lib/Typography/Typography';

import AvailabilityBookStatusInfo from 'spa/Components/AvailabilityBookStatusInfo/AvailabilityBookStatusInfo';

import './therapist-top-info.less';

@withRouter
@inject(({
  store: {
    basketNew,
    screen,
    tracker,
  },
  therapistsState,
}) => ({
  basketNew,
  screen,
  therapistsState,
  tracker,
}))
@observer
@withI18n
class TherapistTopInfo extends React.Component {
  static propTypes = {
    basketNew: PropTypes.shape({
      basketHasAddress: PropTypes.bool,
      basketsWorkerPreValidate: PropTypes.shape({
        load: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    isDesktopMode: PropTypes.bool,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    onClickBook: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    screen: PropTypes.shape({
      isDesktop: PropTypes.bool,
    }).isRequired,
    therapistsState: PropTypes.shape({
      lastBooked: PropTypes.string,
      selectedId: PropTypes.number,
      workerName: PropTypes.string,
      workerRating: PropTypes.string,
    }),
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
      trackCriteoEvent: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string.isRequired,
    workerId: PropTypes.number.isRequired,
  }

  static defaultProps = {
    isDesktopMode: false,
  }

  componentDidMount() {
    const {i18n} = this.context;
    const {
      basketNew,
      notifications,
      trackingCategory,
      therapistsState,
      tracker,
      workerId,
    } = this.props;

    this.listeners = [
      reaction(
        () => this.props.basketNew.basketHasAddress,
        (basketHasAddress) => {
          if (basketHasAddress) {
            this.isLoadingAvailabilityStatus = true;

            basketNew.basketsWorkerPreValidate.load(therapistsState.selectedId).then((response) => {
              tracker.track('Worker available for basket in worker details page', {
                category: trackingCategory,
                page: this.currentLocation,
                worker_id: workerId,
              });

              this.isLoadingAvailabilityStatus = false;
            }).catch((error) => {
              if (error && error.json && error.json.validationFailureCode) {
                this.unavailableReason = i18n.__error({
                  code: error.json.validationFailureCode,
                });
              }

              if (__DEV__) {
                console.error(error);
              }

              this.isLoadingAvailabilityStatus = false;

              if (this.unavailableReason) {
                tracker.track('Worker unavailable for basket in worker details page', {
                  category: trackingCategory,
                  page: this.currentLocation,
                  validationFailureCode: error.json.validationFailureCode,
                  worker_id: workerId,
                });

                return;
              }

              if (error.json && error.json.httpCode !== 404) {
                notifications.add({
                  id: 'getWorkerAvailabilityError',
                  type: 'error',
                  title: i18n.__error(error),
                });

                tracker.track('Error getting worker availability for basket in worker details page', {
                  category: trackingCategory,
                  page: this.currentLocatio,
                  worker_id: workerId,
                });
              }

              // This is unexpected so record it in Sentry
              if (isValidException(error)) {
                Sentry.captureException(error);
              } else {
                Sentry.captureMessage('Unhandled error getting worker availability in the worker details page');
              }
            });
          } else {
            this.isLoadingAvailabilityStatus = false;
          }
        },
        {
          name: 'Check worker availability for basket',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable isLoadingAvailabilityStatus = true;
  @observable unavailableReason;

  @computed get currentLocation() {
    const {router} = this.props;

    if (router && router.location && router.location.pathname) {
      return router.location.pathname;
    }

    return null;
  }

  render() {
    const {
      isDesktopMode,
      onClickBook,
      screen,
      therapistsState: {
        lastBooked,
        workerName,
        workerRating,
      },
    } = this.props;
    const {i18n} = this.context;

    return (
      <React.Fragment>
        <Typography
          align={isDesktopMode ? 'left' : 'center'}
          margin="0 0 2rem"
          variant={TypeVariants.h1}
        >
          {workerName}
        </Typography>
        <div className="therapist-top-info__general-rating-container">
          <StarRatingGroup
            activeStars="1"
            fontSize={RatingFontSizeOptions.large}
            fontWeight={RatingFontWeight.medium}
            iconSize={IconSizes.small}
            includeRatingCount
            ratingCount={workerRating}
            totalStars="1"
          />
        </div>
        {lastBooked && (
          <Typography
            align={isDesktopMode ? 'left' : 'center'}
            margin="1rem 0 0"
            textTheme="secondary"
            variant={TypeVariants.bodyXLarge}
          >
            {i18n._x('Last Booked: %(lastBooked)s', 'Therapist single page', {
              lastBooked,
            })}
          </Typography>
        )}
        <AvailabilityBookStatusInfo
          alignLeft={screen.isDesktop}
          isLoadingStatus={this.isLoadingAvailabilityStatus}
          onClickBookButton={onClickBook}
          trackingCategory="Therapists profile"
          trackingLocation={this.currentLocation}
          unavailableReason={this.unavailableReason}
        />
      </React.Fragment>
    );
  }
}

export default TherapistTopInfo;



// WEBPACK FOOTER //
// ./spa/Pages/Therapists/TherapistTopInfo.jsx